import React, { useCallback, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CancelButton,
  Input,
  SubmitButton,
} from '../../../../../components/form';
import { ModalFooter } from '../../../../../components/Modal';
import {
  TimeEntryNotesFormData,
  useTimeEntryNotesForm,
} from '../../../hooks/useTimeEntryNotesForm';
import {
  ExtraFieldsAreaSimple,
  ExtraFieldsAreaSimpleRef,
} from '../../../components/ExtraFields/ExtraFieldsArea/ExtraFieldsAreaSimple';
import {
  ExtraFieldInternalValue,
  mapToExtraFieldInput,
} from '../../../components/ExtraFields/extraFieldInternalValue';
import {
  ExtraFieldsSupportedObject,
  ExtraFieldsFormId,
  ExtraFieldContextParam,
} from '../../../components/ExtraFields/types';
import { ExtraFieldInput } from '../../../__generated__/graphql';
import { useClockOutRequirements } from '../../../hooks/timeTracking/useClockOutRequirements';

type Props = {
  timeEntryId: string;
  notes: string | null;
  show: boolean;
  onClose: () => void;
  onSubmit: (
    notes: string | null,
    extraFields: ExtraFieldInput[],
  ) => Promise<void>;
  extraFieldValues: readonly ExtraFieldInternalValue[];
  recordTypeName: string | null;
  contextParams: ExtraFieldContextParam[];
};

const StopTrackingModal: React.FC<React.PropsWithChildren<Props>> = ({
  show,
  onClose,
  onSubmit,
  notes,
  extraFieldValues,
  recordTypeName,
  contextParams,
}) => {
  const { t } = useTranslation();

  const { requireNotesOnClockOut } = useClockOutRequirements();
  const { form, fieldName } = useTimeEntryNotesForm({
    notes: notes,
    required: requireNotesOnClockOut,
  });

  const [extraFields, setExtraFields] =
    useState<readonly ExtraFieldInternalValue[]>(extraFieldValues);
  const extraFieldsRef = useRef<ExtraFieldsAreaSimpleRef>(null);

  const handleExtraFieldChange = useCallback(
    (values: ExtraFieldInternalValue[]) => {
      setExtraFields(values);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (form: TimeEntryNotesFormData) => {
      const extraValid =
        (await extraFieldsRef.current?.triggerValidation()) ?? true;
      if (!extraValid) {
        return;
      }
      return onSubmit(form.notes, extraFields.map(mapToExtraFieldInput));
    },
    [onSubmit, extraFields],
  );

  return (
    <Modal show={show} onHide={onClose} centered>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Modal.Header closeButton className="card-header">
            <h4 className="card-header-title">
              {t('timeTracking.current.stopTrackingModal.title')}
            </h4>
          </Modal.Header>
          <Modal.Body>
            <Input
              name={fieldName}
              type="textarea"
              label={t('timeTracking.current.stopTrackingModal.content')}
              placeholder={t(
                'timeTracking.current.stopTrackingModal.notesPlaceholder',
              )}
              minRows={4}
            />
            <div className="mt-3">
              <ExtraFieldsAreaSimple
                ref={extraFieldsRef}
                objectApiName={ExtraFieldsSupportedObject.TimeEntry}
                formId={ExtraFieldsFormId.TimeEntryEdit}
                onChange={handleExtraFieldChange}
                values={extraFields}
                recordTypeName={recordTypeName}
                contextParams={contextParams}
              />
            </div>
          </Modal.Body>
          <ModalFooter>
            <CancelButton size="sm" onClick={onClose}>
              {t('timeTracking.current.stopTrackingModal.cancelButton')}
            </CancelButton>

            <SubmitButton size="sm">
              {t('timeTracking.current.stopTrackingModal.confirmButton')}
            </SubmitButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default StopTrackingModal;
